import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/services", label: "Services" },
    { path: "/about", label: "About Us" },
    { path: "/service-areas", label: "Service Areas" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-header" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center group-hover:bg-emerald-700 transition-colors">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-heading font-bold text-forest-950">
              Nexivas
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? "text-emerald-600"
                    : "text-forest-950 hover:text-emerald-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+1-771-1871-987"
              className="flex items-center gap-2 text-forest-950 hover:text-emerald-600 transition-colors font-medium"
              data-testid="header-phone"
            >
              <Phone className="w-5 h-5" />
              <span>+1-771-1871-987</span>
            </a>
            <Link to="/contact">
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                data-testid="header-get-quote-btn"
              >
                Get Free Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-forest-950"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div 
          className="lg:hidden bg-white border-t border-emerald-100 shadow-lg"
          data-testid="mobile-menu"
        >
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? "bg-emerald-50 text-emerald-600"
                    : "text-forest-950 hover:bg-emerald-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-emerald-100 space-y-3">
              <a
                href="tel:+1-771-1871-987"
                className="flex items-center gap-2 py-3 px-4 text-forest-950 font-medium"
                data-testid="mobile-phone"
              >
                <Phone className="w-5 h-5 text-emerald-600" />
                +1-771-1871-987
              </a>
              <Link to="/contact" className="block">
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                  data-testid="mobile-get-quote-btn"
                >
                  Get Free Quote
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
